import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import {
  ShoppingCart,
  Download,
  FileText,
  Clock,
  CheckCircle,

  Loader2,
  ArrowRight,
  Zap,
  Star,
  Gift
} from "lucide-react";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import QuickBuyModal from "@/components/QuickBuyModal";
import ShareReferralButton from "@/components/ShareReferralButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { addItem, items } = useCart();
  const { user, token } = useAuth();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickBuyOpen, setQuickBuyOpen] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(null);
  const [ratings, setRatings] = useState({ average_rating: 0, total_ratings: 0, reviews: [] });
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);

  const isInCart = product && items.some(item => item.product_id === product.id);

  useEffect(() => {
    fetchProduct();
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      trackReferral(refCode);
    }
  }, [slug]);

  const trackReferral = async (code) => {
    try {
      const response = await axios.get(`${API}/referrals/track/${code}`);
      if (response.data.valid) {
        setReferralDiscount(response.data);
        sessionStorage.setItem("referral_code", code);
      }
    } catch (error) {
      console.error("Invalid referral:", error);
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      setProduct(response.data);
      fetchRelatedProducts(response.data.subject, response.data.class_level);
      fetchRatings(response.data.id);
    } catch (error) {
      console.error("Error fetching product:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async (productId) => {
    try {
      const res = await axios.get(`${API}/products/${productId}/ratings`);
      setRatings(res.data);
      // If user has already rated, set their rating
      if (user) {
        const myReview = res.data.reviews.find(r => r.user_name === user.name);
        if (myReview) {
          setUserRating(myReview.rating);
          setReviewText(myReview.review_text || "");
        }
      }
    } catch (e) { /* ignore */ }
  };

  const submitRating = async () => {
    if (!product || userRating === 0) return;
    setSubmittingRating(true);
    try {
      await axios.post(`${API}/products/${product.id}/rate`, { rating: userRating, review_text: reviewText || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Rating submitted!");
      fetchRatings(product.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  };

  const fetchRelatedProducts = async (subject, classLevel) => {
    try {
      const response = await axios.get(`${API}/products?subject=${encodeURIComponent(subject)}&class_level=${encodeURIComponent(classLevel)}&per_page=4`);
      setRelatedProducts(response.data.products.filter(p => p.slug !== slug));
    } catch (error) {
      console.error("Error fetching related products:", error);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addItem(product);
      toast.success(`${product.title} added to cart`);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="Great Messiah Publication" className="w-16 h-16 mx-auto mb-4 object-contain opacity-40 grayscale" />
          <h2 className="font-heading text-2xl font-bold mb-2">Product Not Found</h2>
          <p className="text-muted-foreground mb-4">The product you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/mock-exams">Browse All Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mock-exams">Mock Exams</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{product.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-[3/4] bg-secondary rounded-2xl overflow-hidden relative">
              {product.cover_image_url ? (
                <img
                  src={resolveImageUrl(product.cover_image_url)}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                  <img src="/logo.png" alt="Great Messiah Publication" className="w-24 h-24 object-contain opacity-30" />
                </div>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.is_featured && (
                  <Badge className="bg-accent text-accent-foreground">
                    <Zap className="w-3 h-3 mr-1" />
                    Popular
                  </Badge>
                )}
                {product.is_bundle && (
                  <Badge variant="secondary">Bundle Pack</Badge>
                )}
              </div>
            </div>

            {/* Preview Pages */}
            {product.preview_pages && product.preview_pages.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.preview_pages.map((url, index) => (
                  <div key={index} className="w-20 h-28 rounded-lg bg-secondary overflow-hidden shrink-0">
                    <img src={url} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{product.subject}</span>
              <span>•</span>
              <span>{product.class_level}</span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="product-title">
              {product.title}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2" data-testid="product-rating">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`w-5 h-5 ${i <= Math.round(ratings.average_rating) ? "fill-accent text-accent" : "text-gray-300"}`} />
                ))}
              </div>
              <span className="text-sm font-medium">{ratings.average_rating > 0 ? ratings.average_rating : "—"}</span>
              <span className="text-sm text-muted-foreground">({ratings.total_ratings} {ratings.total_ratings === 1 ? "review" : "reviews"})</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-primary" data-testid="product-price">
                {formatPrice(product.price_ghs)}
              </span>
              {product.compare_at_price_ghs && product.compare_at_price_ghs > product.price_ghs && (
                <>
                  <span className="text-xl text-muted-foreground line-through">
                    {formatPrice(product.compare_at_price_ghs)}
                  </span>
                  <Badge className="bg-destructive text-destructive-foreground">
                    Save {Math.round((1 - product.price_ghs / product.compare_at_price_ghs) * 100)}%
                  </Badge>
                </>
              )}
            </div>

            {/* Short Description */}
            <p className="text-muted-foreground leading-relaxed">
              {product.short_description || product.description}
            </p>

            {/* Referral Discount Banner */}
            {referralDiscount && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-testid="referral-discount-banner">
                <Gift className="w-6 h-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Referral Discount Applied!</p>
                  <p className="text-sm text-green-600">You get <strong>{referralDiscount.discount_percentage}% off</strong> on this purchase via a friend's referral.</p>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              {product.page_count && (
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <FileText className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="text-lg font-bold">{product.page_count}</p>
                  <p className="text-xs text-muted-foreground">Pages</p>
                </div>
              )}
              {product.question_count && (
                <div className="text-center p-4 bg-secondary/50 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="text-lg font-bold">{product.question_count}</p>
                  <p className="text-xs text-muted-foreground">Questions</p>
                </div>
              )}
              <div className="text-center p-4 bg-secondary/50 rounded-xl">
                <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-lg font-bold">{product.duration_minutes || "—"}</p>
                <p className="text-xs text-muted-foreground">Minutes</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                size="lg"
                className="flex-1 bg-primary hover:bg-primary/90 h-14 text-lg"
                onClick={handleAddToCart}
                disabled={isInCart}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {isInCart ? "Added to Cart" : "Add to Cart"}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="flex-1 h-14 text-lg border-2 border-accent text-accent hover:bg-accent hover:text-white"
                onClick={() => setQuickBuyOpen(true)}
                data-testid="buy-now-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Quick Buy
              </Button>
            </div>

            {product && (
              <QuickBuyModal
                product={product}
                open={quickBuyOpen}
                onOpenChange={setQuickBuyOpen}
              />
            )}

            {/* Share & Earn */}
            {product && (
              <div className="pt-2">
                <ShareReferralButton product={product} />
              </div>
            )}

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Download className="w-5 h-5 text-primary" />
                <span>Instant PDF Download</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-5 h-5 text-primary" />
                <span>GES Aligned</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="description" className="mt-12">
          <TabsList className="w-full justify-start bg-secondary/50 p-1 rounded-xl">
            <TabsTrigger value="description" className="rounded-lg">Description</TabsTrigger>
            <TabsTrigger value="features" className="rounded-lg">Features</TabsTrigger>
            <TabsTrigger value="faq" className="rounded-lg">FAQ</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg" data-testid="reviews-tab">
              Reviews ({ratings.total_ratings})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="description" className="mt-6">
            <div className="prose prose-green max-w-none">
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>
          </TabsContent>

          <TabsContent value="features" className="mt-6">
            {product.features && product.features.length > 0 ? (
              <ul className="space-y-3">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">No features listed.</p>
            )}
          </TabsContent>

          <TabsContent value="faq" className="mt-6">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-2">How do I download the exam?</h4>
                <p className="text-muted-foreground">After payment, you'll receive an email with a download link. The link is valid for 72 hours with up to 5 download attempts.</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Is the content aligned with GES syllabus?</h4>
                <p className="text-muted-foreground">Yes, all our mock examinations are aligned with the Ghana Education Service syllabus (2024 to date).</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">Can I get a refund?</h4>
                <p className="text-muted-foreground">Refunds are available only for corrupted or inaccessible files. Once the download link is used, no refunds are provided.</p>
              </div>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6" data-testid="reviews-content">
            {/* Submit Rating */}
            {user && (
              <div className="bg-secondary/30 rounded-xl p-6 mb-8 border" data-testid="rating-form">
                <h3 className="font-semibold text-lg mb-3">Rate This Product</h3>
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setUserRating(i)}
                      onMouseEnter={() => setHoverRating(i)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-0.5 transition-transform hover:scale-110"
                      data-testid={`rate-star-${i}`}
                    >
                      <Star className={`w-8 h-8 cursor-pointer transition-colors ${i <= (hoverRating || userRating) ? "fill-accent text-accent" : "text-gray-300"}`} />
                    </button>
                  ))}
                  {userRating > 0 && <span className="ml-2 text-sm font-medium text-muted-foreground">{userRating}/5</span>}
                </div>
                <textarea
                  placeholder="Write an optional review..."
                  value={reviewText}
                  onChange={e => setReviewText(e.target.value)}
                  className="w-full rounded-lg border bg-background p-3 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                  rows={3}
                  data-testid="review-textarea"
                />
                <Button onClick={submitRating} disabled={userRating === 0 || submittingRating} className="mt-3" data-testid="submit-rating-btn">
                  {submittingRating ? "Submitting..." : "Submit Rating"}
                </Button>
              </div>
            )}
            {!user && (
              <p className="text-muted-foreground mb-6 text-sm">
                <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link> to rate this product (purchase required).
              </p>
            )}

            {/* Reviews List */}
            {ratings.reviews.length > 0 ? (
              <div className="space-y-4" data-testid="reviews-list">
                {ratings.reviews.map(review => (
                  <div key={review.id} className="border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{review.user_name}</span>
                        <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded font-medium">Verified Purchase</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{new Date(review.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-4 h-4 ${i <= review.rating ? "fill-accent text-accent" : "text-gray-300"}`} />
                      ))}
                    </div>
                    {review.review_text && <p className="text-sm text-muted-foreground">{review.review_text}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No reviews yet. Be the first to review this product!</p>
            )}
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16">
            <h2 className="font-heading text-2xl font-bold mb-6">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
